import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  TextField,
  CircularProgress,
  Collapse,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useDevice } from '../contexts/DeviceContext';
import { scheduleSettingsService } from '../services/scheduleSettingsService';
import {
  DeviceScheduleSettings,
  CompletionAction,
  TimerStyle,
  DayColors,
  DayOfWeek,
  ALL_DAYS_OF_WEEK,
  DEFAULT_SCHEDULE_SETTINGS,
} from '../types/schedule';

// Timer styles available for the default setting (limited per user request)
const AVAILABLE_TIMER_STYLES: TimerStyle[] = ['shrinking_disk', 'shrinking_bar', 'countdown'];

// Completion actions available for default
const AVAILABLE_COMPLETION_ACTIONS: CompletionAction[] = [
  'checkmark',
  'tap',
  'swipe',
  'hold',
  'auto',
  'parent_only',
];

const ScheduleSettingsSection: React.FC = () => {
  const { t } = useTranslation();
  const { selectedDevice, showSnackbar } = useDevice();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<DeviceScheduleSettings>(DEFAULT_SCHEDULE_SETTINGS);

  const isViewer = selectedDevice?.role === 'viewer';

  // Load settings
  const loadSettings = useCallback(async () => {
    if (!selectedDevice?.id) return;

    try {
      setLoading(true);
      const data = await scheduleSettingsService.getScheduleSettings(selectedDevice.id);
      setSettings(data);
    } catch (error) {
      console.error('Failed to load schedule settings:', error);
      showSnackbar(t('scheduleSettings.failedToLoad'), 'error');
    } finally {
      setLoading(false);
    }
  }, [selectedDevice?.id, showSnackbar, t]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  // Save settings
  const saveSettings = useCallback(
    async (updates: Partial<DeviceScheduleSettings>) => {
      if (!selectedDevice?.id || isViewer) return;

      try {
        setSaving(true);
        const updatedSettings = await scheduleSettingsService.updateScheduleSettings(
          selectedDevice.id,
          updates
        );
        setSettings(updatedSettings);
        showSnackbar(t('scheduleSettings.settingsSaved'), 'success');
      } catch (error) {
        console.error('Failed to save schedule settings:', error);
        showSnackbar(t('scheduleSettings.failedToSave'), 'error');
      } finally {
        setSaving(false);
      }
    },
    [selectedDevice?.id, isViewer, showSnackbar, t]
  );

  const handleCompletionActionChange = (value: CompletionAction) => {
    setSettings((prev) => ({ ...prev, defaultCompletionAction: value }));
    saveSettings({ defaultCompletionAction: value });
  };

  const handleTimerStyleChange = (value: TimerStyle) => {
    setSettings((prev) => ({ ...prev, defaultTimerStyle: value }));
    saveSettings({ defaultTimerStyle: value });
  };

  const handleDayColorsEnabledChange = (enabled: boolean) => {
    setSettings((prev) => ({ ...prev, dayColorsEnabled: enabled }));
    saveSettings({ dayColorsEnabled: enabled });
  };

  const handleDayColorChange = (day: DayOfWeek, color: string) => {
    const newDayColors: DayColors = {
      ...settings.dayColors,
      [day]: color,
    };
    setSettings((prev) => ({ ...prev, dayColors: newDayColors }));
    // Debounce color changes slightly
    saveSettings({ dayColors: newDayColors });
  };

  if (!selectedDevice) return null;

  return (
    <Card sx={{ mt: 2 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {t('scheduleSettings.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t('scheduleSettings.description')}
        </Typography>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Default Completion Action */}
            <FormControl fullWidth disabled={isViewer || saving}>
              <InputLabel>{t('scheduleSettings.defaultCompletionAction')}</InputLabel>
              <Select
                value={settings.defaultCompletionAction}
                onChange={(e) => handleCompletionActionChange(e.target.value as CompletionAction)}
                label={t('scheduleSettings.defaultCompletionAction')}
              >
                {AVAILABLE_COMPLETION_ACTIONS.map((action) => (
                  <MenuItem key={action} value={action}>
                    {t(`schedules.completionActions.${action}`)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Default Timer Style */}
            <FormControl fullWidth disabled={isViewer || saving}>
              <InputLabel>{t('scheduleSettings.defaultTimerStyle')}</InputLabel>
              <Select
                value={settings.defaultTimerStyle}
                onChange={(e) => handleTimerStyleChange(e.target.value as TimerStyle)}
                label={t('scheduleSettings.defaultTimerStyle')}
              >
                {AVAILABLE_TIMER_STYLES.map((style) => (
                  <MenuItem key={style} value={style}>
                    {t(`schedules.timerStyles.${style}`)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Day Colors Toggle */}
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.dayColorsEnabled}
                    onChange={(e) => handleDayColorsEnabledChange(e.target.checked)}
                    disabled={isViewer || saving}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1">{t('scheduleSettings.dayColors')}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('scheduleSettings.dayColorsDescription')}
                    </Typography>
                  </Box>
                }
              />
            </Box>

            {/* Day Color Pickers */}
            <Collapse in={settings.dayColorsEnabled}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                  gap: 2,
                  mt: 1,
                  p: 2,
                  bgcolor: 'grey.50',
                  borderRadius: 1,
                }}
              >
                {ALL_DAYS_OF_WEEK.map((day) => (
                  <Box
                    key={day}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                    }}
                  >
                    <Typography sx={{ minWidth: 90 }}>
                      {t(`schedules.daysLong.${day}`)}
                    </Typography>
                    <TextField
                      type="color"
                      value={settings.dayColors[day]}
                      onChange={(e) => handleDayColorChange(day, e.target.value)}
                      disabled={isViewer || saving}
                      sx={{
                        width: 60,
                        '& input': {
                          padding: '4px',
                          height: 32,
                          cursor: 'pointer',
                        },
                      }}
                      size="small"
                    />
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        bgcolor: settings.dayColors[day],
                        border: '1px solid',
                        borderColor: 'grey.300',
                      }}
                    />
                  </Box>
                ))}
              </Box>
            </Collapse>

            {/* Saving indicator */}
            {saving && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} />
                <Typography variant="body2" color="text.secondary">
                  {t('common.saving')}
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default ScheduleSettingsSection;
